/**
 * Tone Pre-Render Cache
 * Uses OfflineAudioContext to pre-render all notification tones and ringtones
 * as AudioBuffers for instant playback. Falls back to WAV files when available.
 */

import { getToneVolume, isTonesMuted } from './aphenisTones';

interface CachedTone {
  buffer: AudioBuffer;
}

const notifCache = new Map<string, CachedTone>();
const ringtoneCache = new Map<string, CachedTone>();

import { getSharedAudioContext, resetSharedAudioContext } from './audioContextPool';

let _ctx: AudioContext | null = null;

function getCtx(): AudioContext {
  _ctx = getSharedAudioContext();
  return _ctx;
}

/** Pre-render a single tone using OfflineAudioContext */
async function renderOffline(
  synth: (ac: BaseAudioContext, t: number, vol: number) => void,
  duration: number,
): Promise<AudioBuffer> {
  const offCtx = new OfflineAudioContext(2, 44100 * duration, 44100);
  synth(offCtx as any, 0, 1.0);
  return offCtx.startRendering();
}

/** Load a WAV file from public/tones/ as an AudioBuffer */
async function loadWavBuffer(path: string): Promise<AudioBuffer | null> {
  try {
    const ac = getCtx();
    const resp = await fetch(path);
    if (!resp.ok) return null;
    const arrayBuf = await resp.arrayBuffer();
    return await ac.decodeAudioData(arrayBuf);
  } catch {
    return null;
  }
}

/** Pre-cache a notification tone from its WAV file */
export async function cacheNotificationTone(id: string): Promise<void> {
  if (notifCache.has(id)) return;
  const buffer = await loadWavBuffer(`/tones/notifications/${id}.wav`);
  if (buffer) notifCache.set(id, { buffer });
}

/** Pre-cache a ringtone from its WAV file */
export async function cacheRingtone(id: string): Promise<void> {
  if (ringtoneCache.has(id)) return;
  const buffer = await loadWavBuffer(`/tones/ringtones/${id}.wav`);
  if (buffer) ringtoneCache.set(id, { buffer });
}

/** Play a cached notification tone. Returns true if confirmed played from cache. */
export function playCachedNotificationTone(id: string): boolean {
  if (isTonesMuted()) return true; // treat as handled
  const cached = notifCache.get(id);
  if (!cached) return false;
  return playBuffer(cached.buffer);
}

/** Play a cached ringtone. Returns true if confirmed played from cache. */
export function playCachedRingtone(id: string): boolean {
  if (isTonesMuted()) return true;
  const cached = ringtoneCache.get(id);
  if (!cached) return false;
  return playBuffer(cached.buffer);
}

function playBuffer(buffer: AudioBuffer): boolean {
  let ac = getCtx();
  const doPlay = (ctx: AudioContext) => {
    const source = ctx.createBufferSource();
    source.buffer = buffer;
    const gain = ctx.createGain();
    gain.gain.setValueAtTime(getToneVolume(), ctx.currentTime);
    source.connect(gain);
    gain.connect(ctx.destination);
    source.start(0);
  };
  if (ac.state === 'suspended') {
    // Try to resume; if it fails, create a fresh context
    ac.resume().then(() => {
      if (ac.state === 'running') {
        doPlay(ac);
      } else {
        console.warn('[toneCache] AudioContext still suspended after resume, creating new context');
        _ctx = resetSharedAudioContext();
        _ctx.resume().then(() => doPlay(_ctx!)).catch(() => {
          console.warn('[toneCache] Playback skipped — no user gesture');
        });
      }
    }).catch(() => {
      console.warn('[toneCache] Resume rejected, trying fresh AudioContext');
      _ctx = resetSharedAudioContext();
      _ctx.resume().then(() => doPlay(_ctx!)).catch(() => {});
    });
    return false; // Can't confirm playback — caller should fall through
  } else {
    doPlay(ac);
    return true; // Confirmed played
  }
}

/** Bulk pre-cache all notification tones (call once at startup) */
export async function preloadAllNotificationTones(ids: string[]): Promise<void> {
  await Promise.allSettled(ids.map(id => cacheNotificationTone(id)));
}

/** Bulk pre-cache all ringtones (call once at startup) */
export async function preloadAllRingtones(ids: string[]): Promise<void> {
  await Promise.allSettled(ids.map(id => cacheRingtone(id)));
}

/** Check if a notification tone is cached */
export function isNotificationToneCached(id: string): boolean {
  return notifCache.has(id);
}

/** Check if a ringtone is cached */
export function isRingtoneCached(id: string): boolean {
  return ringtoneCache.has(id);
}

/** Flush all cached audio buffers to free memory */
export function clearToneCache(): void {
  notifCache.clear();
  ringtoneCache.clear();
}
